import { useState, useEffect, useCallback } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2, Wallet, Info } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { GettingStarted } from "@/components/GettingStarted"
import { ToggleableAmount } from "@/components/ui/toggleable-amount"
import stwelephantLogo from "@/assets/tokens/stwelephant.svg"
import { useEthersContext } from "@/providers/EthersProvider"
import { useTransactionToast } from "@/hooks/useTransactionToast"
import { GAS_LIMIT, getMulticall3AddressForChain, STAKING_VAULT_ADDRESS, createSwapContract } from "@/lib/contracts"
import { stWelToUsdViaRedeem } from "@/lib/ethersUtils"
import { Interface, formatUnits, Contract, Signer } from "ethers"
import { isUserRejection, getWalletErrorMessage } from "@/lib/walletErrors"
import { getChainProvider } from "@/lib/chainProvider"
import { createMulticallService } from "@/services/multicallService"

const GAME_CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID || 56)
const STREAM_VAULT_ADDRESS = import.meta.env.VITE_STREAM_VAULT_ADDRESS

const STREAM_VAULT_ABI = [
  "function getStream(address user) external view returns (tuple(uint256 earnedStWelephant, uint256 claimedStWelephant))",
  "function getClaimable(address user) external view returns (uint256)",
  "function getAffiliateRewards(address referrer) external view returns (uint256)",
  "function claim() external",
  "function messageService() external view returns (address)",
  "function lifetimeReceived(address) external view returns (uint256)",
  "function lifetimeAffiliateFees(address) external view returns (uint256)",
  "function lifetimeClaps(address) external view returns (uint256)",
  "function lifetimeDonated(address) external view returns (uint256)",
  "function donationCount(address) external view returns (uint256)",
] as const

const vaultIface = new Interface(STREAM_VAULT_ABI)

const fmtStWel = (value: bigint) => {
  const f = parseFloat(formatUnits(value, 18))
  if (f === 0) return "0"
  if (f < 0.001) return "<0.001"
  return f.toLocaleString(undefined, { maximumFractionDigits: 3 })
}


interface StreamData {
  earnedStWelephant: bigint
  claimedStWelephant: bigint
}

interface LifetimeStats {
  received: bigint
  affiliateFees: bigint
  claps: bigint
  donated: bigint
  donationCount: bigint
}

export function StreamInfo({ onMessageServiceAddress }: { onMessageServiceAddress?: (addr: string) => void } = {}) {
  const { provider, signer, address, isConnected, providerReady } = useEthersContext()
  const { showTxPending, showTxError } = useTransactionToast()

  const [stream, setStream] = useState<StreamData | null>(null)
  const [claimable, setClaimable] = useState<bigint>(0n)
  const [affiliateRewards, setAffiliateRewards] = useState<bigint>(0n)
  const [lifetime, setLifetime] = useState<LifetimeStats | null>(null)
  const [earnedUsd, setEarnedUsd] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const [showGuide, setShowGuide] = useState(false)

  const fetchData = useCallback(async () => {
    if (!address || !STREAM_VAULT_ADDRESS) return
    setIsLoading(true)
    try {
      const chainProvider = await getChainProvider(GAME_CHAIN_ID)
      const multicall = createMulticallService(chainProvider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)

      const calls = [
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'getStream', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'getClaimable', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'getAffiliateRewards', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'messageService'),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'lifetimeReceived', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'lifetimeAffiliateFees', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'lifetimeClaps', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'lifetimeDonated', [address]),
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, 'donationCount', [address]),
      ]

      const [streamData, claimableAmount, rewards, msgServiceAddr,
        ltReceived, ltAffiliate, ltClaps, ltDonated, ltDonationCount] =
        await multicall.callAndDecode<[
          [bigint, bigint],
          bigint, bigint, string,
          bigint, bigint, bigint, bigint, bigint
        ]>(calls)

      setStream({
        earnedStWelephant: streamData[0],
        claimedStWelephant: streamData[1],
      })
      setClaimable(claimableAmount)
      setAffiliateRewards(rewards)
      setLifetime({
        received: ltReceived,
        affiliateFees: ltAffiliate,
        claps: ltClaps,
        donated: ltDonated,
        donationCount: ltDonationCount,
      })
      if (msgServiceAddr && msgServiceAddr !== "0x0000000000000000000000000000000000000000") {
        onMessageServiceAddress?.(msgServiceAddr)
      }
    } catch (err) {
      console.error("Failed to fetch stream vault data:", err)
    } finally {
      setIsLoading(false)
    }
  }, [address, onMessageServiceAddress])

  useEffect(() => { fetchData() }, [fetchData])

  // Poll every 30s
  useEffect(() => {
    if (!address) return
    const interval = setInterval(fetchData, 30_000)
    return () => clearInterval(interval)
  }, [fetchData, address])

  // Convert earned stWELEPHANT to USD via swap price + previewRedeem
  useEffect(() => {
    if (!provider || !STAKING_VAULT_ADDRESS) return
    const earned = stream?.earnedStWelephant ?? 0n
    if (earned === 0n) { setEarnedUsd(null); return }
    let cancelled = false
    const swap = createSwapContract(provider)
    if (!swap) return
    const stakingContract = new Contract(
      STAKING_VAULT_ADDRESS,
      ["function previewRedeem(uint256 shares) view returns (uint256)"],
      provider,
    )
    const redeem = (shares: bigint) => stakingContract.previewRedeem(shares) as Promise<bigint>
    swap.getWelephantPriceInUSD()
      .then((price: bigint) => stWelToUsdViaRedeem(earned, price, redeem))
      .then((usd) => { if (!cancelled) setEarnedUsd(usd) })
      .catch(() => { if (!cancelled) setEarnedUsd(null) })
    return () => { cancelled = true }
  }, [provider, stream?.earnedStWelephant])

  const handleClaim = async () => {
    if (!provider || !signer || !STREAM_VAULT_ADDRESS) return
    setIsClaiming(true)
    try {
      const contract = new Contract(STREAM_VAULT_ADDRESS, STREAM_VAULT_ABI, signer as Signer)
      const tx = await contract.claim({ gasLimit: GAS_LIMIT })
      const toast = showTxPending("Claim stWELEPHANT", tx.hash)
      await tx.wait()
      toast.success()
      await fetchData()
    } catch (err) {
      if (!isUserRejection(err)) {
        showTxError("Claim stWELEPHANT", getWalletErrorMessage(err, "Claim failed") ?? "Claim failed")
      }
    } finally {
      setIsClaiming(false)
    }
  }

  if (!STREAM_VAULT_ADDRESS) return null

  return (
    <>
    <Card>
      <CardContent className="p-4 relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-7 w-7"
          onClick={() => setShowGuide(true)}
          title="Getting Started Guide"
        >
          <Info className="h-4 w-4 text-muted-foreground" />
        </Button>
        {!providerReady || !isConnected ? (
          !providerReady ? null : (
            <div className="text-center py-6">
              <Wallet className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Connect wallet to view your stream</p>
            </div>
          )
        ) : isLoading && !stream ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : stream ? (
          <div className="space-y-4">
            {stream.earnedStWelephant === 0n ? (
              /* No raised funds yet — show vanity setup directions */
              <GettingStarted embedded path="/assets/docs/vanity-getting-started.md" />
            ) : (
              /* Headline: Raised */
              <div className="pt-2 text-center">
                <div className="flex items-center justify-center gap-2">
                  <img src={stwelephantLogo} alt="" className="h-7 w-7" />
                  <ToggleableAmount
                    amount={fmtStWel(stream.earnedStWelephant)}
                    symbol="stWELEPHANT"
                    hideLabel
                    usdValue={earnedUsd}
                    amountClassName="text-3xl font-semibold tracking-tight"
                  />
                </div>
                <div className="text-xs text-muted-foreground mt-1">Raised</div>
              </div>
            )}

            {affiliateRewards > 0n && (
              <div className="p-2 bg-primary/5 rounded-lg text-sm">
                <span className="text-muted-foreground">Affiliate Rewards: </span>
                <span className="font-medium">{fmtStWel(affiliateRewards)} stWELEPHANT</span>
              </div>
            )}

            {/* Claim section */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Claimable</span>
                <span className="font-semibold flex items-center gap-1">
                  <img src={stwelephantLogo} alt="" className="h-4 w-4" />
                  {fmtStWel(claimable)}
                </span>
              </div>
              <Button
                onClick={handleClaim}
                disabled={isClaiming || claimable === 0n}
                className="w-full"
                size="sm"
              >
                {isClaiming ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-1" />Claiming...</>
                ) : (
                  "Claim"
                )}
              </Button>
            </div>

            {/* Lifetime stats */}
            {lifetime && (
              <div className="border-t pt-3 space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground mb-2">Lifetime</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Donated</span>
                    <span className="font-medium flex items-center gap-1">
                      <img src={stwelephantLogo} alt="" className="h-3 w-3" />
                      {fmtStWel(lifetime.donated)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Affiliate</span>
                    <span className="font-medium flex items-center gap-1">
                      <img src={stwelephantLogo} alt="" className="h-3 w-3" />
                      {fmtStWel(lifetime.affiliateFees)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Likes</span>
                    <span className="font-medium">{Number(lifetime.claps).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Donations</span>
                    <span className="font-medium">{Number(lifetime.donationCount).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">No stream data</p>
        )}
      </CardContent>
    </Card>

      <Dialog modal={false} open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Getting Started</DialogTitle>
          </DialogHeader>
          <GettingStarted embedded />
        </DialogContent>
      </Dialog>
    </>
  )
}
